import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, MagnifyingGlass, Package } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, ProgressBar } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProductForm from "./ProductForm";
import { useCurrencySymbol } from "@/hooks/useCurrencySymbol";

const FALLBACK_IMG = "https://images.pexels.com/photos/8633662/pexels-photo-8633662.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Products() {
  const currencySymbol = useCurrencySymbol();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [formOpen, setFormOpen] = useState(false);

  const { data: products, isLoading } = useQuery({
    queryKey: ["products", search, status],
    queryFn: async () =>
      (await api.get("/products", { params: { search: search || undefined, status: status === "all" ? undefined : status } })).data,
  });

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter text-slate-900">Products</h1>
          <p className="mt-1 text-sm text-slate-500">{products?.length ?? 0} product(s) in the pipeline</p>
        </div>
        {can("products.create") && (
          <Button onClick={() => setFormOpen(true)} data-testid="new-product-button" className="bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
            <Plus size={16} className="mr-1" /> New Product
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row" data-testid="products-filters">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="products-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…" className="pl-9" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-44" data-testid="products-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">In Progress</SelectItem>
            <SelectItem value="published">Published</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-sm text-slate-400">Loading products…</div>
      ) : (products || []).length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="products-empty">
          <Package size={40} weight="duotone" className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No products found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="products-grid">
          {(products || []).map((p) => (
            <Link key={p.id} to={`/products/${p.id}`} data-testid={`product-card-${p.id}`}
              className="group rounded-md border border-slate-200 bg-white transition-all duration-200 hover:-translate-y-[1px] hover:shadow-md">
              <div className="relative h-36 overflow-hidden rounded-t-md">
                <img src={p.images?.[0] || FALLBACK_IMG} alt={p.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute right-2 top-2"><StatusBadge status={p.status} /></div>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-heading text-base font-bold tracking-tight text-slate-900 leading-snug">{p.name}</h3>
                  {p.price > 0 && <span className="shrink-0 text-sm font-semibold text-slate-700">{currencySymbol}{p.price}</span>}
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {p.category && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{p.category}</span>}
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{p.egg_type === "egg" ? "Egg" : "Eggless"} · {p.diet_type === "non_veg" ? "Non-Veg" : "Veg"}</span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <ProgressBar value={p.progress} />
                  <span className="shrink-0 text-xs font-semibold text-slate-600">{p.completed_steps}/{p.total_steps} steps</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <ProductForm open={formOpen} onOpenChange={setFormOpen} product={null} onSaved={() => queryClient.invalidateQueries({ queryKey: ["products"] })} />
    </div>
  );
}
